import { useState, useEffect, useCallback } from 'react';
import apiClient from '@/lib/api/client';

interface RFI {
  id: number;
  rfi_number: string;
  subject: string;
  question: string;
  response: string | null;
  status: 'open' | 'answered' | 'closed';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  submitted_by: string | null;
  due_date: string | null;
  answered_at: string | null;
  answered_by: string | null;
  created_at: string;
}

interface RFIsTabProps {
  jobId: string | number;
}

const fmtDate = (d: string | null) =>
  d ? new Date(d).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const statusBadge: Record<RFI['status'], string> = {
  open: 'bg-blue-100 text-blue-700',
  answered: 'bg-green-100 text-green-700',
  closed: 'bg-gray-100 text-gray-500',
};

const priorityBadge: Record<RFI['priority'], string> = {
  low: 'bg-gray-100 text-gray-500',
  normal: 'bg-blue-50 text-blue-600',
  high: 'bg-orange-100 text-orange-700',
  urgent: 'bg-red-100 text-red-700',
};

const priorityLabel: Record<RFI['priority'], string> = {
  low: 'Low',
  normal: 'Normal',
  high: 'High',
  urgent: 'Urgent',
};

// ── Delete Confirm Modal ──────────────────────────────────────────────────────

interface DeleteModalProps {
  onConfirm: () => void;
  onCancel: () => void;
  deleting: boolean;
}

const DeleteModal = ({ onConfirm, onCancel, deleting }: DeleteModalProps) => (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    onClick={e => { if (e.target === e.currentTarget && !deleting) onCancel(); }}
  >
    <div className="w-full max-w-sm rounded-2xl bg-white shadow-xl p-6 space-y-4">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100 text-red-600">
          <span className="material-symbols-outlined text-xl">delete</span>
        </span>
        <h2 className="text-base font-bold text-gray-900">Delete RFI</h2>
      </div>
      <p className="text-sm text-gray-600">
        Are you sure you want to delete this RFI? This cannot be undone.
      </p>
      <div className="flex justify-end gap-3">
        <button
          onClick={onCancel}
          disabled={deleting}
          className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={deleting}
          className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-bold hover:bg-red-700 disabled:opacity-60 transition-colors"
        >
          {deleting ? 'Deleting…' : 'Delete'}
        </button>
      </div>
    </div>
  </div>
);

// ── New RFI Form ──────────────────────────────────────────────────────────────

interface NewRFIFormProps {
  jobId: number;
  onSaved: () => void;
  onCancel: () => void;
}

const emptyNewForm = () => ({
  subject: '',
  question: '',
  priority: 'normal' as RFI['priority'],
  submitted_by: '',
  due_date: '',
});

const NewRFIForm = ({ jobId, onSaved, onCancel }: NewRFIFormProps) => {
  const [form, setForm] = useState(emptyNewForm());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.subject.trim()) { setError('Subject is required.'); return; }
    if (!form.question.trim()) { setError('Question is required.'); return; }
    setSaving(true);
    setError('');
    try {
      await apiClient.post(`/jobs/${jobId}/rfis`, {
        subject: form.subject.trim(),
        question: form.question.trim(),
        priority: form.priority,
        submitted_by: form.submitted_by.trim() || undefined,
        due_date: form.due_date || undefined,
      });
      onSaved();
    } catch {
      setError('Failed to create RFI.');
      setSaving(false);
    }
  };

  const field = (label: string, required = false) => (
    <label className="block text-xs font-semibold text-gray-600 mb-1">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
  );

  const inputCls =
    'w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-60';

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
      <h3 className="text-sm font-bold text-gray-900">New Request for Information</h3>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">{error}</div>
      )}

      <div>
        {field('Subject', true)}
        <input
          type="text"
          value={form.subject}
          onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
          disabled={saving}
          placeholder="Brief subject line"
          className={inputCls}
        />
      </div>

      <div>
        {field('Question', true)}
        <textarea
          value={form.question}
          onChange={e => setForm(f => ({ ...f, question: e.target.value }))}
          disabled={saving}
          rows={4}
          placeholder="Describe the information requested…"
          className={`${inputCls} resize-none`}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          {field('Priority')}
          <select
            value={form.priority}
            onChange={e => setForm(f => ({ ...f, priority: e.target.value as RFI['priority'] }))}
            disabled={saving}
            className={inputCls}
          >
            <option value="low">Low</option>
            <option value="normal">Normal</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
        </div>
        <div>
          {field('Submitted By')}
          <input
            type="text"
            value={form.submitted_by}
            onChange={e => setForm(f => ({ ...f, submitted_by: e.target.value }))}
            disabled={saving}
            placeholder="Name or company"
            className={inputCls}
          />
        </div>
        <div>
          {field('Due Date')}
          <input
            type="date"
            value={form.due_date}
            onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))}
            disabled={saving}
            className={inputCls}
          />
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-1">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-bold hover:bg-primary/90 disabled:opacity-60 transition-colors"
        >
          {saving ? 'Submitting…' : 'Submit RFI'}
        </button>
      </div>
    </form>
  );
};

// ── Answer Form ───────────────────────────────────────────────────────────────

interface AnswerFormProps {
  rfi: RFI;
  jobId: number;
  onSaved: () => void;
  onCancel: () => void;
}

const AnswerForm = ({ rfi, jobId, onSaved, onCancel }: AnswerFormProps) => {
  const [response, setResponse] = useState(rfi.response ?? '');
  const [answeredBy, setAnsweredBy] = useState(rfi.answered_by ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!response.trim()) { setError('Response is required.'); return; }
    setSaving(true);
    setError('');
    try {
      await apiClient.patch(`/jobs/${jobId}/rfis/${rfi.id}`, {
        response: response.trim(),
        answered_by: answeredBy.trim() || undefined,
        status: 'answered',
      });
      onSaved();
    } catch {
      setError('Failed to save response.');
      setSaving(false);
    }
  };

  const inputCls =
    'w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-60';

  return (
    <form onSubmit={handleSubmit} className="mt-3 bg-gray-50 rounded-lg border border-gray-200 p-4 space-y-3">
      <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide">Answer RFI</h4>
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-2.5 text-sm text-red-700">{error}</div>
      )}
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1">
          Response <span className="text-red-500">*</span>
        </label>
        <textarea
          value={response}
          onChange={e => setResponse(e.target.value)}
          disabled={saving}
          rows={4}
          placeholder="Provide a detailed response…"
          className={`${inputCls} resize-none`}
        />
      </div>
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1">Answered By</label>
        <input
          type="text"
          value={answeredBy}
          onChange={e => setAnsweredBy(e.target.value)}
          disabled={saving}
          placeholder="Name or role"
          className={inputCls}
        />
      </div>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-medium text-gray-700 hover:bg-white disabled:opacity-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="px-3 py-1.5 rounded-lg bg-green-600 text-white text-xs font-bold hover:bg-green-700 disabled:opacity-60 transition-colors"
        >
          {saving ? 'Saving…' : 'Save Answer'}
        </button>
      </div>
    </form>
  );
};

// ── RFI Row ───────────────────────────────────────────────────────────────────

interface RFIRowProps {
  rfi: RFI;
  jobId: number;
  onRefresh: () => void;
  onDeleteRequest: (id: number) => void;
}

const RFIRow = ({ rfi, jobId, onRefresh, onDeleteRequest }: RFIRowProps) => {
  const [expanded, setExpanded] = useState(false);
  const [showAnswerForm, setShowAnswerForm] = useState(false);
  const [toggling, setToggling] = useState(false);

  const handleToggleStatus = async () => {
    setToggling(true);
    try {
      const nextStatus = rfi.status === 'closed' ? 'open' : 'closed';
      await apiClient.patch(`/jobs/${jobId}/rfis/${rfi.id}`, { status: nextStatus });
      onRefresh();
    } catch {
      // silently fail — parent will keep current state
    } finally {
      setToggling(false);
    }
  };

  const handleAnswerSaved = () => {
    setShowAnswerForm(false);
    setExpanded(true);
    onRefresh();
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header row */}
      <div
        className="flex flex-wrap items-center gap-3 px-5 py-4 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setExpanded(e => !e)}
      >
        <span className="material-symbols-outlined text-gray-400 text-lg shrink-0">
          {expanded ? 'expand_less' : 'expand_more'}
        </span>

        <span className="text-xs font-mono font-semibold text-gray-400 shrink-0 w-20">{rfi.rfi_number}</span>

        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 truncate">{rfi.subject}</p>
          <p className="text-xs text-gray-400 mt-0.5">
            {rfi.submitted_by ? `Submitted by ${rfi.submitted_by} · ` : ''}
            {fmtDate(rfi.created_at)}
            {rfi.due_date ? ` · Due ${fmtDate(rfi.due_date)}` : ''}
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0" onClick={e => e.stopPropagation()}>
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${priorityBadge[rfi.priority]}`}>
            {priorityLabel[rfi.priority]}
          </span>
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${statusBadge[rfi.status]}`}>
            {rfi.status}
          </span>

          {rfi.status === 'open' && (
            <button
              onClick={() => { setShowAnswerForm(a => !a); setExpanded(true); }}
              className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-green-50 border border-green-200 text-xs font-semibold text-green-700 hover:bg-green-100 transition-colors"
            >
              <span className="material-symbols-outlined text-base leading-none">reply</span>
              Answer
            </button>
          )}

          <button
            onClick={handleToggleStatus}
            disabled={toggling}
            className="flex items-center gap-1 px-2.5 py-1 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            <span className="material-symbols-outlined text-base leading-none">
              {rfi.status === 'closed' ? 'lock_open' : 'lock'}
            </span>
            {rfi.status === 'closed' ? 'Reopen' : 'Close'}
          </button>

          <button
            onClick={() => onDeleteRequest(rfi.id)}
            className="flex items-center gap-1 px-2.5 py-1 rounded-lg border border-red-200 text-xs font-semibold text-red-600 hover:bg-red-50 transition-colors"
          >
            <span className="material-symbols-outlined text-base leading-none">delete</span>
            Delete
          </button>
        </div>
      </div>

      {/* Expanded body */}
      {expanded && (
        <div className="px-5 pb-5 border-t border-gray-100">
          <div className="pt-4 space-y-4">
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Question</p>
              <p className="text-sm text-gray-800 whitespace-pre-wrap">{rfi.question}</p>
            </div>

            {rfi.response && (
              <div className="bg-green-50 rounded-lg border border-green-100 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-xs font-semibold text-green-700 uppercase tracking-wide">Response</p>
                  {rfi.answered_by && (
                    <span className="text-xs text-green-600">by {rfi.answered_by}</span>
                  )}
                  {rfi.answered_at && (
                    <span className="text-xs text-green-500">· {fmtDate(rfi.answered_at)}</span>
                  )}
                </div>
                <p className="text-sm text-gray-800 whitespace-pre-wrap">{rfi.response}</p>
              </div>
            )}

            {showAnswerForm && (
              <AnswerForm
                rfi={rfi}
                jobId={jobId}
                onSaved={handleAnswerSaved}
                onCancel={() => setShowAnswerForm(false)}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Main Tab ──────────────────────────────────────────────────────────────────

export const RFIsTab = ({ jobId }: RFIsTabProps) => {
  const numericJobId = typeof jobId === 'string' ? parseInt(jobId, 10) : jobId;

  const [rfis, setRfis] = useState<RFI[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showNewForm, setShowNewForm] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const { data } = await apiClient.get(`/jobs/${numericJobId}/rfis`);
      setRfis(data.data ?? data);
    } catch {
      setError('Failed to load RFIs.');
    } finally {
      setLoading(false);
    }
  }, [numericJobId]);

  useEffect(() => { load(); }, [load]);

  const handleSaved = async () => {
    setShowNewForm(false);
    await load();
  };

  const handleDelete = async () => {
    if (deleteId === null) return;
    setDeleting(true);
    try {
      await apiClient.delete(`/jobs/${numericJobId}/rfis/${deleteId}`);
      setDeleteId(null);
      await load();
    } catch {
      setError('Failed to delete RFI.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-gray-900 text-[22px] font-bold leading-tight tracking-[-0.015em]">
          Requests for Information
        </h2>
        {!showNewForm && (
          <button
            onClick={() => setShowNewForm(true)}
            className="flex items-center gap-2 h-9 px-4 rounded-lg bg-primary text-white text-sm font-bold hover:bg-primary/90 transition-colors"
          >
            <span className="material-symbols-outlined text-base">add</span>
            New RFI
          </button>
        )}
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
      )}

      {showNewForm && (
        <NewRFIForm
          jobId={numericJobId}
          onSaved={handleSaved}
          onCancel={() => setShowNewForm(false)}
        />
      )}

      {loading ? (
        <div className="py-12 text-center">
          <span className="material-symbols-outlined animate-spin text-3xl text-gray-300 block mx-auto mb-2">
            progress_activity
          </span>
          <p className="text-sm text-gray-400">Loading…</p>
        </div>
      ) : rfis.length === 0 && !showNewForm ? (
        <div className="py-16 bg-white rounded-xl border border-gray-200 text-center">
          <span className="material-symbols-outlined text-5xl text-gray-300 block mb-3">quiz</span>
          <p className="text-sm text-gray-500 mb-4">No RFIs yet for this job.</p>
          <button
            onClick={() => setShowNewForm(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white text-sm font-bold hover:bg-primary/90 transition-colors"
          >
            <span className="material-symbols-outlined text-base">add</span>
            Submit First RFI
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {rfis.map(rfi => (
            <RFIRow
              key={rfi.id}
              rfi={rfi}
              jobId={numericJobId}
              onRefresh={load}
              onDeleteRequest={id => setDeleteId(id)}
            />
          ))}
        </div>
      )}

      {deleteId !== null && (
        <DeleteModal
          onConfirm={handleDelete}
          onCancel={() => setDeleteId(null)}
          deleting={deleting}
        />
      )}
    </div>
  );
};
